import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Button,
  ColorPicker,
  Field,
  KanbanTag,
  Modal,
  ModalActions,
} from '@evogard/ui';
import type { Department, Label } from '@evogard/contract';
import { LABEL_NAME_MAX, loadFailureMessage, type LabelColor } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

/**
 * As etiquetas de um setor (07/10/2026) — a lista que quem é dele põe nos
 * cards, quantas quiser em cada um.
 *
 * Abre pela linha do departamento, como "Adicionar pessoas": a etiqueta é do
 * setor, e é aqui que se monta o setor. Nasce vazia — o catálogo está no
 * Pipefy, não no repositório.
 *
 * **Nada é apagado.** Arquivar tira a etiqueta do seletor, e o card que já a
 * tem continua mostrando; quem é do setor pode tirá-la de lá. Reativar a
 * devolve ao seletor.
 */
export function DepartmentLabelsModal({
  department,
  onClose,
}: {
  department: Department;
  onClose: () => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();
  const key = ['plataforma', 'departamentos', tenantId, 'etiquetas', department.id];
  /* As etiquetas do card leem esta lista: trocar aqui recarrega lá também. */
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: key }),
      client.invalidateQueries({ queryKey: ['sinistros'] }),
    ]);

  const labels = useQuery({
    queryKey: key,
    queryFn: async (): Promise<Label[]> => {
      const r = await api.platform.listDepartmentLabels({
        params: { id: department.id },
        query: { includeArchived: 'true' },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'as etiquetas'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });

  const toggle = useMutation({
    mutationFn: async (label: Label) =>
      ok(
        await api.platform.updateDepartmentLabel({
          params: { id: department.id, labelId: label.id },
          body: { archived: label.archivedAt === null },
        }),
        200,
        'Não foi possível arquivar a etiqueta.',
      ),
    onSuccess: refresh,
  });

  /** A etiqueta em edição na linha; `null` é nenhuma. */
  const [editing, setEditing] = useState<number | null>(null);
  const list = labels.data ?? [];
  const active = list.filter((l) => !l.archivedAt);
  const archived = list.filter((l) => l.archivedAt);

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Etiquetas de ${department.nome}`}
      descricao="Quem é deste departamento põe estas etiquetas nos cards — quantas quiser em cada um."
      icon="etiqueta"
      tone="destaque"
      size="md"
    >
      <div className="flex flex-col gap-5">
        <LabelForm
          departmentId={department.id}
          submitLabel="Criar etiqueta"
          onSaved={refresh}
        />

        {labels.isError && <Alert tone="erro">{labels.error.message}</Alert>}
        {toggle.isError && <Alert tone="erro">{toggle.error.message}</Alert>}

        <section aria-label="Etiquetas do departamento" className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
            {labels.data ? `${active.length} ativas` : 'Carregando…'}
          </h3>
          {labels.data && active.length === 0 && (
            <p className="text-sm text-texto-suave">
              Nenhuma etiqueta ainda. Crie a primeira acima.
            </p>
          )}
          <ul className="flex flex-col divide-y divide-borda-suave">
            {[...active, ...archived].map((label) =>
              editing === label.id ? (
                <li key={label.id} className="py-3">
                  <LabelForm
                    departmentId={department.id}
                    label={label}
                    submitLabel="Salvar"
                    onSaved={async () => {
                      await refresh();
                      setEditing(null);
                    }}
                    onCancel={() => setEditing(null)}
                  />
                </li>
              ) : (
                <li
                  key={label.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <KanbanTag color={label.color}>{label.name}</KanbanTag>
                    {label.archivedAt && (
                      <span className="text-xs text-texto-suave">arquivada</span>
                    )}
                  </span>
                  <span className="flex gap-1">
                    {!label.archivedAt && (
                      <Button
                        size="sm"
                        variant="discreto"
                        onClick={() => setEditing(label.id)}
                      >
                        Editar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="discreto"
                      loading={toggle.isPending && toggle.variables?.id === label.id}
                      onClick={() => toggle.mutate(label)}
                    >
                      {label.archivedAt ? 'Reativar' : 'Arquivar'}
                    </Button>
                  </span>
                </li>
              ),
            )}
          </ul>
        </section>

        <ModalActions className="border-t border-borda-suave pt-5">
          <Button variant="secundario" onClick={onClose}>
            Fechar
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}

/** Criar (sem `label`) ou editar uma etiqueta: o nome, a cor e a prévia. */
function LabelForm({
  departmentId,
  label,
  submitLabel,
  onSaved,
  onCancel,
}: {
  departmentId: string;
  label?: Label;
  submitLabel: string;
  onSaved: () => Promise<unknown>;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(label?.name ?? '');
  const [color, setColor] = useState<LabelColor>(label?.color ?? 'azul');

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), color };
      return label
        ? ok(
            await api.platform.updateDepartmentLabel({
              params: { id: departmentId, labelId: label.id },
              body,
            }),
            200,
            'Não foi possível salvar a etiqueta.',
          )
        : ok(
            await api.platform.createDepartmentLabel({ params: { id: departmentId }, body }),
            201,
            'Não foi possível criar a etiqueta.',
          );
    },
    onSuccess: async () => {
      await onSaved();
      if (!label) setName('');
    },
  });

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) save.mutate();
      }}
    >
      <Field
        label={label ? 'Nome da etiqueta' : 'Nova etiqueta'}
        required
        autoFocus={Boolean(label)}
        maxLength={LABEL_NAME_MAX}
        placeholder="Ex.: Aguardando cliente"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={save.error?.message}
      />
      <ColorPicker value={color} onChange={setColor} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span aria-hidden className="flex items-center gap-2 text-xs text-texto-suave">
          Prévia: <KanbanTag color={color}>{name.trim() || 'Etiqueta'}</KanbanTag>
        </span>
        <span className="flex gap-2">
          {onCancel && (
            <Button type="button" size="sm" variant="secundario" onClick={onCancel}>
              Cancelar
            </Button>
          )}
          <Button
            type="submit"
            size="sm"
            loading={save.isPending}
            disabled={name.trim() === ''}
          >
            {submitLabel}
          </Button>
        </span>
      </div>
    </form>
  );
}
